import React, { useContext, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Snackbar } from '@material-ui/core';
import { SnackbarContent } from '../../styled-components/shared';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Modal } from '../common/Modal';
import { createPrimaryItem, createDangerItem, createGhostItem } from '../common/modalItems';
import { MessageResponseContext } from '../../data/MessageLocalizationProvider';
import { AuthContext } from '../../authentication/Auth';
import { usePermissions, ROLES } from '../../authentication/rbac';
import firebaseUtil from '../../data/FirebaseUtil';
import { generatePassword } from '../../data/userUtil';
import app from '../../authentication/firebaseStart';
import { USER_TYPE } from '../../data/constants';
import '@zebra-fed/icons/Alert/index.css';
import { InputField } from '../common/InputField';
import { DropdownField } from '../common/DropdownField';
import ConfirmModal from '../modals/ConfirmModal';
var emailValidator = require('email-validator');

const firestore = app.firestore();

const useStyles = makeStyles(() => ({
    scrollableContent: {
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
    },
    mainContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    sectionHeader: {
        fontSize: 14,
        fontWeight: 400,
        margin: 0,
        color: '#636e72',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    row: {
        display: 'flex',
        gap: 8,
    }
}));

const defaultDialogProps = {
    width: '640px',
};

function UserFormModal({
    modalRef,
    onClose,
    action = 'CREATE_USER',
    initialData = null,
    onUserCreated = null,
    onUserUpdated = null
}) {
    const { t } = useTranslation();
    const classes = useStyles();
    const { getKeyItem } = useContext(MessageResponseContext);
    const context = useContext(AuthContext);
    const { userRole, canCreateUser, canUpdateUser } = usePermissions();

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [allSiteNames, setAllSiteNames] = useState([{ label: t('selectSite'), value: '' }]);
    const [isSSO, setSSO] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const randomPass = useRef(generatePassword());
    const confirmModalRef = useRef();
    const serverMessage = useRef('');

    const ACTIONS = {
        CREATE_USER: 'CREATE_USER',
        EDIT_USER: 'EDIT_USER'
    };
    
    const isEditMode = action === ACTIONS.EDIT_USER;

    const getRoleOptions = () => {
        const baseOptions = [
            { value: '', label: t('selectRole'), isdisabled: true }
        ];

        if ([ROLES.ZEBRA_SUPER_USER, ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(userRole)) {
            return [
                ...baseOptions,
                { value: ROLES.ADMIN, label: t('admin') },
                { value: ROLES.MANAGER, label: t('manager') },
                { value: ROLES.COMPANY_USER, label: t('companyUser') }
            ];
        }
        return baseOptions;
    };

    const roleOptions = getRoleOptions();
    const userTypeOptions = [
        { value: USER_TYPE.NON_SSO, label: t('nonsso') },
        { value: USER_TYPE.SSO, label: t('sso') },
    ];
    const languageOptions = [{ value: 'en', label: t('language.en') }];

    const validationSchema = Yup.object().shape({
        firstName: Yup.string()
            .required(t('firstNameRequired'))
            .min(1, t('firstNameMinLength'))
            .max(50, t('firstNameMaxLength'))
            .matches(/^[a-zA-Z0-9'-]+(\s[a-zA-Z0-9'-]+)*$/, t('enterValidFirstName'))
            .test('trimmed-validation', t('enterValidFirstName'), value =>
                value?.trim().length > 0
            ),
        lastName: Yup.string()
            .required(t('lastNameRequired'))
            .min(1, t('lastNameMinLength'))
            .max(50, t('lastNameMaxLength'))
            .matches(/^[a-zA-Z0-9'-]+(\s[a-zA-Z0-9'-]+)*$/, t('enterValidLastName'))
            .test('trimmed-validation', t('enterValidLastName'), value =>
                value?.trim().length > 0
            ),
        email: Yup.string()
            .email(t('invalidEmail'))
            .required(t('emailRequired'))
            .test('is-valid-email', t('invalidEmail'), function (value) {
                return emailValidator.validate(value);
            }),
        role: Yup.string().required(t('roleRequired')),
        siteName: Yup.string().when('role', {
            is: 'manager',
            then: () => Yup.string().required(t('siteRequired')),
        }),
        authType: Yup.string().required(t('userTypeRequired')),
        language: Yup.string().required(t('languageRequired')),
    });

    const getInitialValues = () => {
        if (isEditMode && initialData) {
            return {
                firstName: initialData.firstName || '',
                lastName: initialData.lastName || '',
                email: initialData.emailId || '',
                role: initialData.accessType || '',
                siteName: initialData.siteName || '',
                authType: Number(initialData.authType) || (isSSO ? USER_TYPE.SSO : USER_TYPE.NON_SSO),
                language: initialData.language || 'en',
                disabled: initialData.disabled
            };
        }

        return {
            firstName: '',
            lastName: '',
            email: '',
            role: '',
            siteName: '',
            authType: isSSO ? USER_TYPE.SSO : USER_TYPE.NON_SSO,
            language: 'en',
            password: randomPass.current,
            disabled: true,
        };
    };

    const formik = useFormik({
        initialValues: getInitialValues(),
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            const trimmedValues = {
                ...values,
                firstName: values.firstName.trim().replace(/\s+/g, ' '),
                lastName: values.lastName.trim().replace(/\s+/g, ' '),
                email: values.email.trim()
            };
            handleSubmit(trimmedValues);
        },
    });

    useEffect(() => {
        if (canCreateUser || canUpdateUser) {
            getAppSettings();
            getSitesData();
        }
    }, [action]); // eslint-disable-line react-hooks/exhaustive-deps

    async function getAppSettings() {
        const companyID = context.currentUser.claims.companyID;
        const appSettings = (
            await firestore.collection('tenants').doc(companyID).collection('Configuration').doc('AppSettings').get()
        ).data();
        const ssoStatus = appSettings ? appSettings.SSOActivationStatus : false;
        setSSO(ssoStatus);
        if (!isEditMode) {
            formik.setFieldValue('authType', ssoStatus ? USER_TYPE.SSO : USER_TYPE.NON_SSO);
        }
    }

    async function getSitesData() {
        const companyID = context.currentUser.claims.companyID;
        try {
            const siteLocationsSnapshot = await firestore
                .collection('tenants')
                .doc(companyID)
                .collection('SiteLocations')
                //.limit(10) // Commented to fix DGN-753
                .get();
            const sitesData = [{ label: t('selectSite'), value: '' }];
            siteLocationsSnapshot.forEach((doc) => {
                sitesData.push({ label: doc.data().siteName, value: doc.data().siteName });
            });
            setAllSiteNames(sitesData);
        } catch (err) {
            console.error('Error getting documents', err);
        }
    }

    async function getDisplaySiteName(siteName, role) {
        if (role !== 'manager') {
            return '';
        }
    
        const companyID = context.currentUser.claims.companyID;
        if (!siteName || !companyID || siteName === t('selectSite')) {
            return '';
        }
    
        try {
            const firestore = app.firestore();
            const selectedSite = siteName.toUpperCase();
            const siteSnapshot = await firestore
                .collection('tenants')
                .doc(companyID)
                .collection('SiteLocations')
                .where('siteName', '==', selectedSite)
                .get();
    
            const siteData = siteSnapshot.docs[0]?.data();
            return siteData?.displaySiteName || '';
        } catch (error) {
            console.error("Error fetching site display name:", error);
            return '';
        }
    }

    async function handleSubmit(values) {
        setIsLoading(true);

        values.authType = values.authType.toString();
        values.displaySiteName = await getDisplaySiteName(values.siteName, values.role);

        try {
            let response;
            if (isEditMode) {
                response = await firebaseUtil.updateUser(values, t);
            } else {
                response = await firebaseUtil.createUser(values, t);
            }
            
            setIsLoading(false);

            if (response.status) {
                onClose(response.statusCode);
                modalRef.current.toggleDialog();
                formik.resetForm();
                if (isEditMode) {
                    onUserUpdated &&  onUserUpdated();
                } else {
                    onUserCreated &&  onUserCreated();
                }
            } else {
                serverMessage.current = response.msg;
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} user:`, error);
            setIsLoading(false);
            setSnackbarOpen(true);
        }
    }

    const handleStatusChange = (newDisabledStatus) => {
        setConfirmAction({
            status: newDisabledStatus,
            message: newDisabledStatus 
                ? t('deactivateUserConfirmationMsg', { email: formik.values.email }) 
                : t('activateUserConfirmationMsg', { email: formik.values.email })
        });
        confirmModalRef.current.toggleDialog();
    };

    const handleConfirmStatusChange = async () => {
        confirmModalRef.current.toggleDialog();
        setIsLoading(true);
        
        try {
            const response = await firebaseUtil.updateUserStatus(formik.values.email, formik.values.authType.toString(), confirmAction.status, t);
            
            if (response.status) {
                formik.setFieldValue('disabled', confirmAction.status);
                onClose();
                modalRef.current.toggleDialog();
                onUserUpdated && onUserUpdated()
            } else {
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error('Error updating user status:', error);
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    async function getSelectInputSearch(rawInput, type) {
        if (type === 'ENTERHIT' && rawInput) {
            const companyID = context.currentUser.claims.companyID;
            setIsLoading(true);
            try {
                const sites = (
                    await firestore
                        .collection('tenants')
                        .doc(companyID)
                        .collection('SiteLocations')
                        .orderBy('siteName')
                        .startAt(rawInput.toUpperCase())
                        .endAt(rawInput.toUpperCase() + '\uf8ff')
                        .limit(10)
                        .get()
                ).docs.map((doc) => doc.data());

                const finalSitesArray = [
                    { label: t('selectSite'), value: '' },
                    ...sites.map((site) => ({ label: site.siteName, value: site.siteName })),
                ];
                setAllSiteNames(finalSitesArray);
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        }
    }

    const handleCancel = () => {
        modalRef.current.toggleDialog();
        formik.resetForm();
    };

    const isFormValid = () => {
        // For edit mode, we don't need to check touched fields since they're pre-populated
        if (isEditMode) {
            return formik.isValid && Object.keys(formik.errors).length === 0;
        }
        // For create mode, we want to ensure fields have been interacted with
        return formik.isValid &&
               Object.keys(formik.touched).length > 0 &&
               Object.keys(formik.errors).length === 0;
    };

    const getModalActions = () => {
        const actions = [
            createGhostItem({
                onClick: handleCancel,
                disabled: isLoading,
                children: t('cancel'),
            })
        ];

        if (isEditMode) {
            actions.push(
                formik.values.disabled
                ? createPrimaryItem({
                    onClick: () => handleStatusChange(false),
                    disabled: isLoading || !isFormValid(),
                    children: t('activate')
                })
                : createDangerItem({
                    onClick: () => handleStatusChange(true),
                    disabled: isLoading || !isFormValid(),
                    children: t('deactivate')
                }),
                createPrimaryItem({
                    onClick: formik.handleSubmit,
                    disabled: isLoading || !isFormValid(),
                    children: t('update'),
                })
            );
        } else {
            actions.push(
                createPrimaryItem({
                    onClick: () => {
                        formik.setFieldValue('disabled', true);
                        formik.handleSubmit();
                    },
                    disabled: isLoading || !isFormValid(),
                    children: t('create'),
                }),
                createPrimaryItem({
                    onClick: () => {
                        formik.setFieldValue('disabled', false);
                        formik.handleSubmit();
                    },
                    disabled: isLoading || !isFormValid(),
                    children: t('createAndActivate'),
                })
            );
        }

        return actions;
    };

    // Return null if user doesn't have required permissions
    if ((!canCreateUser && !isEditMode) || (!canUpdateUser && isEditMode)) {
        return null;
    }

    return (
        <>
            <Modal
                modalRef={modalRef}
                title={isEditMode ? t('editUser') : t('createUser')}
                actions={getModalActions()}
                dialogProps={{
                    ...defaultDialogProps,
                    style: { cursor: isLoading ? 'wait' : 'default' },
                }}
            >
                <div className={classes.scrollableContent}>
                    <div className={classes.mainContent}>
                        <div className={classes.section}>
                            <div className={classes.row}>
                                <DropdownField
                                    label={t('role')}
                                    fullWidth={true}
                                    required={true}
                                    options={roleOptions}
                                    value={formik.values.role}
                                    onChange={(event) => formik.setFieldValue('role', event.target.value)}
                                    error={formik.touched.role && formik.errors.role}
                                    helperText={formik.touched.role && formik.errors.role}
                                    isDisabled={isEditMode}
                                    openDropDownInBody = {true}
                                />
                                <DropdownField
                                    label={t('userType')}
                                    fullWidth={true}
                                    required={true}
                                    options={userTypeOptions}
                                    value={formik.values.authType}
                                    onChange={(event) => formik.setFieldValue('authType', event.target.value)}
                                    error={formik.touched.authType && formik.errors.authType}
                                    helperText={formik.touched.authType && formik.errors.authType}
                                    isDisabled={isEditMode || (!isEditMode && !isSSO)}
                                    openDropDownInBody = {true}
                                />
                            </div>
                        </div>

                        <div className={classes.section}>
                            <Typography className={classes.sectionHeader}>
                                {t('basicInformation')}
                            </Typography>
                            <div className={classes.row}>
                                <InputField
                                    label={t('firstName')}
                                    fullWidth={true}
                                    required={true}
                                    {...formik.getFieldProps('firstName')}
                                    error={formik.touched.firstName && formik.errors.firstName}
                                    helperText={formik.touched.firstName && formik.errors.firstName}
                                    inputProps={{maxLength: 50}}
                                />
                                <InputField
                                    label={t('lastName')}
                                    fullWidth={true}
                                    required={true}
                                    {...formik.getFieldProps('lastName')}
                                    error={formik.touched.lastName && formik.errors.lastName}
                                    helperText={formik.touched.lastName && formik.errors.lastName}
                                    inputProps={{maxLength: 50}}
                                />
                            </div>
                            <InputField
                                label={t('email')}
                                fullWidth={true}
                                required={true}
                                {...formik.getFieldProps('email')}
                                error={formik.touched.email && formik.errors.email}
                                helperText={formik.touched.email && formik.errors.email}
                                disabled={isEditMode}
                            />
                            {formik.values.role === 'manager' && (
                                <DropdownField
                                    label={t('site.siteName')}
                                    fullWidth={true}
                                    required={true}
                                    options={allSiteNames}
                                    value={formik.values.siteName}
                                    onChange={(event) => formik.setFieldValue('siteName', event.target.value)}
                                    isSearchable={true}
                                    onSearchInpChange={getSelectInputSearch}
                                    isLoading={isLoading}
                                    error={formik.touched.siteName && formik.errors.siteName}
                                    helperText={formik.touched.siteName && formik.errors.siteName}
                                    openDropDownInBody = {true}
                                />
                            )}
                        </div>

                        <div className={classes.section}>
                            <Typography className={classes.sectionHeader}>
                                {t('others')}
                            </Typography>
                            <DropdownField
                                label={t('language.label')}
                                fullWidth
                                required={true}
                                options={languageOptions}
                                value={formik.values.language}
                                onChange={(event) => formik.setFieldValue('language', event.target.value)}
                                error={formik.touched.language && formik.errors.language}
                                helperText={formik.touched.language && formik.errors.language}
                                openDropDownInBody = {true}
                            />
                        </div>
                    </div>
                </div>
            </Modal>

            <ConfirmModal
                modalRef={confirmModalRef}
                title={confirmAction?.status ? t('deactivateUser') : t('activateUser')}
                message={confirmAction?.message}
                onConfirm={handleConfirmStatusChange}
            />

            <Snackbar 
                open={snackbarOpen} 
                autoHideDuration={3000} 
                onClose={() => {setSnackbarOpen(false); serverMessage.current = ""}}
            >
                <SnackbarContent error={true}>
                    {serverMessage.current ?? t(isEditMode ? 'Error updating user' : 'Error creating user')}
                </SnackbarContent>
            </Snackbar>
        </>
    );
}

export default UserFormModal;